import ol_source_ImageCanvas from 'ol/source/ImageCanvas.js';
import * as olExtent from 'ol/extent';
import ol_ext_Worker from './Worker.js';
import * as olProj from 'ol/proj';
import axios from 'axios';

/** Inverse distance weighting interpolated source for RGB channels using three vector sources
 * @constructor 
 * @extends {ol_source_ImageCanvas}
 * @param {*} [options]
 *  @param {ol.source.Vector} options.redSource Source for red channel values
 *  @param {ol.source.Vector} options.greenSource Source for green channel values
 *  @param {ol.source.Vector} options.blueSource Source for blue channel values
 *  @param {function} [options.getColor] Function that takes R, G, B values and returns [R, G, B, A]
 *  @param {boolean} [options.useWorker=false] Use worker for calculation
 *  @param {number} [options.scale=4] Scale factor for performance
 *  @param {number} [options.maxD] Maximum interpolation distance
 *  @param {string|function} options.weight Feature attribute for weight
 */
var ol_source_IDW = class olsourceIDW extends ol_source_ImageCanvas {
  constructor(options) {
    options = options || {};

    options.canvasFunction = function(extent, resolution, pixelRatio, size) {
      return this.canvasFunction(extent, resolution, pixelRatio, size);
    };
    // options.canvasFunction = this.canvasFunction.bind(this);
    super(options);
    

    this._currentTaskId = null;
    this._isRendering = false;
    this._cache = null;
    this._taskParams = new Map(); // Add this line
    // Монотонный счётчик запросов интерполяции. Каждый /interpolate и каждый
    // /change_params (пан/зум) получает новый id; в live-режиме SSE-обработчик
    // применяет пришедший кадр только если его request_id совпадает с
    // последним запрошенным — так картинка, посчитанная для устаревшего
    // положения карты, отбрасывается вместо ошибочного наложения.
    this._requestSeq = 0;
    this._latestRequestId = 0;
    this._latestExtent = null;

    this.set('projection', options.projection || 'EPSG:3857');

    this._redSource = options.redSource;
    this._greenSource = options.greenSource;
    this._blueSource = options.blueSource;
    this._canvas = document.createElement('CANVAS');

    this._redRange = options.redRange;
    this._greenRange = options.greenRange;
    this._blueRange = options.blueRange;

    this._redFilter = options.redFilter;
    this._greenFilter = options.greenFilter;
    this._blueFilter = options.blueFilter;

    this._redSteps = options.redSteps || 1;
    this._greenSteps = options.greenSteps || 1;
    this._blueSteps = options.blueSteps || 1;

    this._redPalette = options.redPalette || {
        base_color: '#ff0000',
        num_colors: 14,
        use_palette: true
    };
    this._greenPalette = options.greenPalette || {
        base_color: '#00ff00',
        num_colors: 14,
        use_palette: true
    };
    this._bluePalette = options.bluePalette || {
        base_color: '#0000ff',
        num_colors: 14,
        use_palette: true
    };
    this._redFormula = options.red_formula;
    this._greenFormula = options.green_formula;
    this._blueFormula = options.blue_formula;

    this._redScaleType = options.redScale || 'linear';
    this._greenScaleType = options.greenScale || 'linear';
    this._blueScaleType = options.blueScale || 'linear';

    // Add scaling functions
    this._redScale = this._parseScale(options.redScale || 'linear');
    this._greenScale = this._parseScale(options.greenScale || 'linear');
    this._blueScale = this._parseScale(options.blueScale || 'linear');

    this._start_time = options.start_time;
    this._end_time  = options.end_time;
    this._avg_time  = options.avg_time;
    this._interval_time  = options.interval;
    // Тип лазера из поля "Laser, nm". Уходит в /interpolate как `laser`;
    // бэкенд превращает его в фильтр по ell_18_2_pos. '' = без фильтра.
    this._laser = options.laser || '';
    this._isUpdateParams = options.isUpdateParams || false;

    // // Listen to all sources for changes
    // const handler = () => this.changed();
    // [this._redSource, this._greenSource, this._blueSource].forEach(source => {
    //   source.on(['addfeature', 'removefeature', 'clear', 'removefeature'], handler);
    // });

    if (typeof options.getColor === 'function') this.getColor = options.getColor;

    if (options.useWorker) {
      this.worker = new ol_ext_Worker(this.computeImage, {
        onMessage: this.onImageData.bind(this),
        lib: { getColor: this.getColor }
      });
    }
    this._position = { extent: [], resolution: 0, pixelRatio : 0, size: []};
    this.set('scale', parseFloat(options.scale) || 4);
    this.set('maxD', parseFloat(options.maxD) || 0);
    this.set('isServerSideCalc', Boolean(options.useServerSideCalc) || false);
    this.set('is_cdom', Boolean(options.is_cdom) || false);
    this.set('is_chla', Boolean(options.is_chla) || false);
    this.set('is_tss', Boolean(options.is_tss) || false);
    this._weight = typeof options.weight === 'function' ? options.weight : f => f.get(options.weight || 'weight');
    this.set('isWaterMask', Boolean(options.isWaterMask));
    this.set('isLive', Boolean(options.isLive));
    // Per-channel калибровки — пробрасываются в /interpolate, чтобы бэкенд
    // применил полином y=a*x^3+b*x^2+c*x+d к результату формулы каждого канала.
    this.set('calibrationIdR', options.calibrationIdR || null);
    this.set('calibrationIdG', options.calibrationIdG || null);
    this.set('calibrationIdB', options.calibrationIdB || null);
  }

  // Override canvasFunction to handle async operations
  canvasFunction(extent, resolution, pixelRatio, size) {
    if (!this.get('isServerSideCalc')) {
      return this.calculateImage(extent, resolution, pixelRatio, size);
    }
    const isFirstRender = this._currentParams ? false : true;
    // Store latest parameters
    this._currentParams = { extent, resolution, pixelRatio, size };

    if(isFirstRender)
      this.updateCanvas(false);

    if(this._isUpdateParams && this._taskParams) {
      this.updateCanvas(true);
      this._isUpdateParams = false
    }
    
    if(this.get('changed')) return;
    
    const canvas = this._canvas;
    this._canvas = null;
    return canvas || this.createBlankCanvas(size);
  }
  createBlankCanvas(size) {
    const canvas = document.createElement('canvas');
    canvas.width = size[0];
    canvas.height = size[1];
    return canvas;
  }
  setChanged(dist) {
    this.set('changed', Boolean(dist) || 0);
    this.changed();
  }
  setUpdateParams(isUpdateParams) {
    this._isUpdateParams = isUpdateParams
  }
  // New method to trigger async rendering
  async updateCanvas(isUpdateParams) {
    if (!this._currentParams || this._isRendering) {
      return;
    }
  
    this._isRendering = true;
    const { extent, resolution, pixelRatio, size } = this._currentParams;

    try {
      if(isUpdateParams)
        await this._requestParamUpdates(extent, resolution, pixelRatio, size)
      else {
        const taskId = await this._requestInterpolation(extent, resolution, pixelRatio, size);
        if (this.get('isLive')) {
          this._setupEventSource(taskId);
        }
        else
          await this._pollForResult(taskId);

      }
      
    } catch (error) {
      console.error('Rendering failed:', error);
    } finally {
      this._isRendering = false;
      //this.changed(); // Notify OpenLayers to redraw
    }
  }
  setRedBaseColor(color) {
      this._redPalette.base_color = color;
      this.setChanged(true);
  }

  setGreenBaseColor(color) {
      this._greenPalette.base_color = color;
      this.setChanged(true);
  }

  setBlueBaseColor(color) {
      this._bluePalette.base_color = color;
      this.setChanged(true);
  }

  setPaletteColorCount(count) {
      this._redPalette.num_colors = count;
      this._greenPalette.num_colors = count;
      this._bluePalette.num_colors = count;
      this.setChanged(true);
  }

  setPaletteConfig(channel, config) {
      switch(channel) {
          case 'red':
              this._redPalette = { ...this._redPalette, ...config };
              break;
          case 'green':
              this._greenPalette = { ...this._greenPalette, ...config };
              break;
          case 'blue':
              this._bluePalette = { ...this._bluePalette, ...config };
              break;
      }
      this.setChanged(true);
  }
  setFormulaes(red, green, blue) {
    this._redFormula = red;
    this._greenFormula = green;
    this._blueFormula = blue;
  }
  setCalibrationIds(r, g, b) {
    this.set('calibrationIdR', r || null);
    this.set('calibrationIdG', g || null);
    this.set('calibrationIdB', b || null);
  }

  // Helper methods
  _getCacheKey(extent, resolution, size) {
    return `${extent.join(',')}|${resolution}|${size.join(',')}`;
  }

  // Existing methods: setScale, setMaxD, getSource, getValue
  /** Set IDW max distance
   * @param {number} [dist] max distance in proj units
   */
  setMaxD(dist) {
    this.set('maxD', parseFloat(dist) || 0);
    //this.changed();
  }
  setScaleGlobal(dist) {
    this.set('scale', parseFloat(dist) || 0);
    //this.changed();
  }
  setIsServerSideCalc(dist) {
    this.set('setIsServerSideCalc', Boolean(dist) || false);
    //this.changed();
  }
  setTimeRange(start ,end) {
    this._start_time = start;
    this._end_time = end;
  }
  setAvgTime(avg) {
    this._avg_time = avg;
  }
  setIntervalTime(interval) {
    this._interval_time = interval;
  }
  setLaser(laser) {
    this._laser = laser || '';
  }
  setIsColor(is_r, is_g, is_b) {
    this.set('is_cdom', Boolean(is_r) || false);
    this.set('is_chla', Boolean(is_g) || false);
    this.set('is_tss', Boolean(is_b) || false);
  }


  /** Get the source
   */
  getSource() {
    return this._source;
  }
  /** Get image value at coord (RGBA)
   * @param {l.coordinate} coord
   * @return {Uint8ClampedArray}
   */
  getValue(coord) {
    if (!this._canvas) return null;
    var pt = this.transform(coord);
    var v = this._canvas.getContext('2d').getImageData(Math.round(pt[0]), Math.round(pt[1]), 1, 1).data;
    return (v);
  }

  /** Parse scale option into a function */
  _parseScale(scale) {
    if (typeof scale === 'function') return scale;
    switch(scale.toLowerCase()) {
      case 'log':
        return v => Math.log10(1 + v); // log10(1 + v) to avoid negative infinity
      case 'sqrt':
        return v => Math.sqrt(v);
      case 'linear':
      default:
        return v => v;
    }
  }

  setSources(r, g, b) {
    this._redSource = r;
    this._greenSource = g;
    this._blueSource = b;
    // this.changed();
  }
  /** Set scaling function for a channel
   * @param {string} channel 'red', 'green', or 'blue'
   * @param {string|function} scale 'linear', 'log', 'sqrt' or custom function
   */
  setScale(channel, scale) {
    this[`_${channel}Scale`] = this._parseScale(scale);
    this[`_${channel}ScaleType`] = scale;
    //this.changed();
  }

  setRanges(redRange, greenRange, blueRange) {
    this._redRange = redRange;
    this._greenRange = greenRange;
    this._blueRange = blueRange;
    //this.changed();
  }
  setFilters(redFilter, greenFilter, blueFilter) {
    this._redFilter = redFilter;
    this._greenFilter = greenFilter;
    this._blueFilter = blueFilter;
  }
  setIntervals(redIntervals, greenIntervals, blueIntervals) {
    this._redIntervals = redIntervals;
    this._greenIntervals = greenIntervals;
    this._blueIntervals = blueIntervals;
    //this.changed();
  }
  setIsWaterMask(is_mask) {
    this.set('isWaterMask', Boolean(is_mask));
  }
  setIsLive(is_live) {
    this.set('isLive', Boolean(is_live));
  }
  async _requestParamUpdates(extent, resolution, pixelRatio, size) {
    // Каждое изменение экстента (пан/зум) — новый запрос. Запоминаем его как
    // «последний запрошенный», чтобы отбросить кадры старого extent в SSE.
    const requestId = ++this._requestSeq;
    const width = Math.round(size[0] / (this.get('scale') * pixelRatio));
    const height = Math.round(size[1] / (this.get('scale') * pixelRatio));

    // Convert maxD (meters) to projection units
    let maxD = 0;
    const maxDInMeters = this.get('maxD');
    if (maxDInMeters) {
      const proj = olProj.get(this.get('projection'));
      const center = [
        (extent[0] + extent[2]) / 2,
        (extent[1] + extent[3]) / 2
      ];
      // Get meters per projection unit at this location
      const metersPerUnit = olProj.getPointResolution(proj, 1, center);
      // Convert maxD (meters) to projection units
      maxD = maxDInMeters / metersPerUnit;
      // Convert to pixels
      maxD = maxD / (this.get('scale') * resolution);
    }
    const requestData = {
      width: width,
      height: height,
      max_d: maxD,
      scale: this.get('scale'),
      projection: this.get('projection'),
      extent: extent,
      resolution: resolution,
      red_palette: this._redPalette,
      green_palette: this._greenPalette,
      blue_palette: this._bluePalette,
      red_formula: this._redFormula,
      green_formula: this._greenFormula,
      blue_formula: this._blueFormula,
      interval: this._interval_time,
      // Per-channel калибровки — нужно прокидывать и при изменении параметров,
      // иначе при смене кривой и нажатии Apply воркер продолжит использовать
      // ранее выбранную (или отсутствующую) калибровку.
      calibration_id_r: this.get('calibrationIdR') || null,
      calibration_id_g: this.get('calibrationIdG') || null,
      calibration_id_b: this.get('calibrationIdB') || null,
      request_id: requestId,
    };
    const it = this._taskParams.keys();
    const taskId = it.next().value;
    if(taskId) {
      // Отправляем PATCH ПЕРЕД тем как двигать _latestRequestId. Если запрос
      // не дошёл (например, упал CORS-preflight), бэкенд продолжит слать кадры
      // со старым request_id — и мы должны их принимать, а не отбрасывать.
      // Иначе один сбойный /change_params навсегда «осиротит» SSE-поток.
      await axios.patch(`/change_params/${taskId}`, requestData);
      // PATCH прошёл — теперь это положение карты считается последним
      // запрошенным; кадры старого extent будут отброшены в SSE-обработчике.
      this._latestRequestId = requestId;
      this._latestExtent = extent;
    }
    else
      return


    // Store parameters associated with this task
    this._taskParams.set(taskId, {
      originalSize: size,
      scale: this.get('scale'),
      pixelRatio: pixelRatio,
      originalWidth: size[0],  // Original pixel dimensions
      originalHeight: size[1],
      extent: extent,
      requestId: requestId
    });
  }

  async _requestInterpolation(extent, resolution, pixelRatio, size) {
    if (!this.get('is_cdom') && !this.get('is_chla') && !this.get('is_tss')) return this._canvas;

    // Новый id запроса — становится «последним запрошенным» положением карты.
    const requestId = ++this._requestSeq;
    this._latestRequestId = requestId;
    this._latestExtent = extent;

    const width = Math.round(size[0] / (this.get('scale') * pixelRatio));
    const height = Math.round(size[1] / (this.get('scale') * pixelRatio));

    // Convert maxD (meters) to projection units
    let maxD = 0;
    const maxDInMeters = this.get('maxD');
    if (maxDInMeters) {
      const proj = olProj.get(this.get('projection'));
      const center = [
        (extent[0] + extent[2]) / 2,
        (extent[1] + extent[3]) / 2
      ];
      // Get meters per projection unit at this location
      const metersPerUnit = olProj.getPointResolution(proj, 1, center);
      // Convert maxD (meters) to projection units
      maxD = maxDInMeters / metersPerUnit;
      // Convert to pixels
      maxD = maxD / (this.get('scale') * resolution);
    }
    const requestData = {
      // red_pts: this._collectPoints(this._redSource, extent, width, height),
      // green_pts: this._collectPoints(this._greenSource, extent, width, height),
      // blue_pts: this._collectPoints(this._blueSource, extent, width, height),
      is_cdom: this.get('is_cdom'),
      is_chla: this.get('is_chla'),
      is_tss: this.get('is_tss'),
      red_scale: this._redScaleType,  // Add scale types
      green_scale: this._greenScaleType,
      blue_scale: this._blueScaleType,
      red_intervals: this._redIntervals?.map(i => ({
        min: i.min,
        max: i.max,
        color: i.color // Send full RGB array
      })),
      green_intervals: this._greenIntervals?.map(i => ({
        min: i.min,
        max: i.max,
        color: i.color // Full RGB
      })),
      blue_intervals: this._blueIntervals?.map(i => ({
        min: i.min,
        max: i.max,
        color: i.color // Full RGB
      })),
      width: width,
      height: height,
      max_d: maxD,
      scale: this.get('scale'),
      projection: this.get('projection'),
      extent: extent,
      red_range: this._redRange,
      green_range: this._greenRange,
      blue_range: this._blueRange,
      r_filter: this._redFilter,
      g_filter: this._greenFilter,
      b_filter: this._blueFilter,
      start_time: this._start_time,
      end_time: this._end_time,
      avg_time: this._avg_time,
      interval: this._interval_time,
      // Пустая строка = фильтра по лазеру нет (бэкенд трактует её как None).
      laser: this._laser || null,
      red_palette: this._redPalette,
      green_palette: this._greenPalette,
      blue_palette: this._bluePalette,
      red_formula: this._redFormula,
      green_formula: this._greenFormula,
      blue_formula: this._blueFormula,
      isWaterMask: this.get('isWaterMask'),
      isLive: this.get('isLive'),
      resolution: resolution,
      // Per-channel калибровки: бэкенд применит свой полином к каждой формуле.
      calibration_id_r: this.get('calibrationIdR') || null,
      calibration_id_g: this.get('calibrationIdG') || null,
      calibration_id_b: this.get('calibrationIdB') || null,
      request_id: requestId,
    };

    const { data } = await axios.post('/interpolate', requestData);
    const taskId = data.task_id;
    this.dispatchEvent({
      type: 'taskstatus',
      status: 'taskstarted',
      taskId: taskId
    });
    
    // Store parameters associated with this task
    this._taskParams.set(taskId, {
      originalSize: size,
      scale: this.get('scale'),
      pixelRatio: pixelRatio,
      originalWidth: size[0],  // Original pixel dimensions
      originalHeight: size[1],
      extent: extent,
      requestId: requestId
    });

    return taskId;
  }

  async _pollForResult(taskId, cacheKey) {
    const params = this._taskParams.get(taskId);
    if (!params) {
      console.error('No parameters found for task', taskId);
      return;
    }
    const {data} = await axios.get(`/result/${taskId}`)

    if (data.status === 'completed') {

      // Verify parameters haven't changed during processing
      if (!olExtent.equals(params.extent, this._currentParams.extent) ||
          params.originalWidth !== this._currentParams.size[0] ||
          params.originalHeight !== this._currentParams.size[1]) {
        // Emit completion event
        this.dispatchEvent({
          type: 'taskstatus',
          status: 'taskcompleted',
          taskId: taskId
        });
        return;
      }
      if (data.palette_info) {
        this.dispatchEvent({
            type: 'paletteinfo',
            paletteInfo: data.palette_info
        });
      }

      const binaryString = atob(data.result);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
      }
      const { originalWidth, originalHeight, } = params;

      // Create canvas with original dimensions
      const canvas = document.createElement('canvas');
      canvas.width = originalWidth;
      canvas.height = originalHeight;
      const ctx = canvas.getContext('2d');

      // Scale up the server's image to original size
      const img = await createImageBitmap(new Blob([bytes], { type: 'image/png' }));
      
      // Draw scaled image
      ctx.drawImage(
        img,
        0, 0, img.width, img.height,         // Source rectangle
        0, 0, originalWidth, originalHeight  // Destination rectangle
      );

      this._canvas = canvas;
      this.changed();
      this._taskParams.delete(taskId); // Cleanup
      // Emit completion event
      this.dispatchEvent({
        type: 'taskstatus',
        status: 'taskcompleted',
        taskId: taskId,
        progress: 100
      });
    }
    else if (data.status === 'pending' || data.status === 'progress') {
      setTimeout(() => this._pollForResult(taskId, cacheKey), 500);
      // Emit completion event
      this.dispatchEvent({
        type: 'taskstatus',
        status: 'taskstarted',
        taskId: taskId,
        progress: data.progress
      });
    }
  }

  async _cancelInterpolation() {
    try {
      const it = this._taskParams.keys();
      const taskId = it.next().value;
      
      if (taskId) {
        const { data } = await axios.delete(`/task/${taskId}`);
        if (data.status === "success") {
          this._taskParams.delete(taskId);
        }
      }
      
      // Остановите streaming если активен
      this.stopStreaming();
      
      // Всегда отправляйте событие отмены
      this.dispatchEvent({
        type: 'taskstatus',
        status: 'taskcancelled',
        taskId: taskId || 'unknown'
      });
      
    } catch (error) {
      console.error('Error cancelling interpolation:', error);
      // Все равно отправьте событие отмены
      this.dispatchEvent({
        type: 'taskstatus',
        status: 'taskcancelled',
        taskId: 'error'
      });
    }
  }

  _setupEventSource(taskId) {
      this._eventSource = new EventSource(process.env.VUE_APP_API_URL + `/stream-events/${taskId}`);
      const params = this._taskParams.get(taskId);
      this._eventSource.onmessage = async (e) => {
          if (e.data.startsWith('ERROR:')) {
              console.error('Stream error:', e.data.substring(6));
              return;
          }
          const response = JSON.parse(e.data)

          // Отбрасываем кадры, посчитанные для устаревшего положения карты
          // (пользователь сдвинул/зумнул карту, пока шёл расчёт). Иначе старую
          // картинку растянет на новый extent → географическое смещение.
          // Надёжный путь — сравнение request_id; fallback по extent оставлен
          // на случай старого бэкенда без тега в payload.
          if (typeof response.request_id === 'number') {
            if (response.request_id !== this._latestRequestId) return;
          } else if (response.extent && this._currentParams &&
                     !olExtent.equals(response.extent, this._currentParams.extent)) {
            return;
          }

          if (response.palette_info) {
            this.dispatchEvent({
                type: 'paletteinfo',
                paletteInfo: response.palette_info
            });
          }

          const binaryString = atob(response.image);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
          }
          // Размеры canvas берём из АКТУАЛЬНЫХ параметров рендера, а не из
          // захваченных при открытии SSE — иначе после зума картинка рисуется
          // в старом размере и искажается. Кадр уже признан актуальным выше,
          // поэтому _currentParams соответствует его extent.
          const renderSize = (this._currentParams && this._currentParams.size) ||
                             [params && params.originalWidth, params && params.originalHeight];
          const originalWidth = renderSize[0];
          const originalHeight = renderSize[1];

          // Create canvas with original dimensions
          const canvas = document.createElement('canvas');
          canvas.width = originalWidth;
          canvas.height = originalHeight;
          const ctx = canvas.getContext('2d');

          // Scale up the server's image to original size
          const img = await createImageBitmap(new Blob([bytes], { type: 'image/png' }));

          // Draw scaled image
          ctx.drawImage(
            img,
            0, 0, img.width, img.height,         // Source rectangle
            0, 0, originalWidth, originalHeight  // Destination rectangle
          );

          this._canvas = canvas;
          this.setChanged(false);
          this.changed();
          this.dispatchEvent({
            type: 'points',
            status: 'points_updated',
            data: response.data
          });
      };
      
      this._eventSource.onerror = (error) => {
          console.log('Stream closed');
          console.error('Error:', error);
          this.stopStreaming();
      };
  }

  stopStreaming() {
      if (this._eventSource) {
          this._eventSource.close();
          // axios.delete(`/api/task/${this._streamTaskId}`);
          this._streamTaskId = null;
          this._eventSource = null;
      }
  }

  _collectPoints(source, extent, width, height) {
    return source.getFeatures().map(f => {
      const coord = f.getGeometry().getFirstCoordinate();
      const x = ((coord[0] - extent[0]) * width) / (extent[2] - extent[0]);
      const y = ((coord[1] - extent[3]) * height) / (extent[1] - extent[3]);
      return [x, y, this._weight(f)];
    });
  }

  calculateImage(extent, resolution, pixelRatio, size) {

    if (!this._redSource || !this._greenSource || !this._blueSource) return this._canvas;

    const width = Math.round(size[0] / (this.get('scale') * pixelRatio));
    const height = Math.round(size[1] / (this.get('scale') * pixelRatio));

    // Convert maxD (meters) to projection units
    let maxD = 0;
    const maxDInMeters = this.get('maxD');
    if (maxDInMeters) {
      const proj = olProj.get(this.get('projection'));
      const center = [
        (extent[0] + extent[2]) / 2,
        (extent[1] + extent[3]) / 2
      ];
      // Get meters per projection unit at this location
      const metersPerUnit = olProj.getPointResolution(proj, 1, center);
      // Convert maxD (meters) to projection units
      maxD = maxDInMeters / metersPerUnit;
      // Convert to pixels
      maxD = maxD / (this.get('scale') * resolution);
    }
    const tr = (xy, v) => [
      ((xy[0] - extent[0]) * width) / (extent[2] - extent[0]),
      ((xy[1] - extent[3]) * height) / (extent[1] - extent[3]),
      v
    ];

    const collectPoints = source => source.getFeatures().map(f => {
      const coord = f.getGeometry().getFirstCoordinate();
      return tr(coord, this._weight(f));
    });

    const message = {
      redPts: collectPoints(this._redSource),
      greenPts: collectPoints(this._greenSource),
      bluePts: collectPoints(this._blueSource),
      width, height,
      // maxD: this.get('maxD') ? this.get('maxD') / this.get('scale') / resolution : 0
      maxD: maxD
    };

    if (this.worker) {
      this.worker.postMessage(message, true);
      this.dispatchEvent({ type: 'drawstart' });
      this._canvas.width = size[0];
      this._canvas.height = size[1];
    } else {
      this._canvas.width = size[0];
      this._canvas.height = size[1];
      this.onImageData(this.computeImage({ data: message }));
    }
    this.dispatchEvent({
        type: 'taskcompleted',
        taskId: 'client-side'
      });
    return this._canvas;
  }

  /** Get color from R, G, B values (clamped to 0-255)
   * @param {number} r Red value
   * @param {number} g Green value
   * @param {number} b Blue value
   * @returns {Array<number>} [R, G, B, 255]
   */
  getColor(r, g, b) {
    var nr, ng, nb;

    // 1. Check if only one channel is active
    if(this._redIntervals) {
      nr = this._normalize(r, this._redRange[0], this._redRange[1], this._redIntervals);
      return [...nr, 255];
    }
    if (this._greenIntervals) {
      ng = this._normalize(g, this._greenRange[0], this._greenRange[1], this._greenIntervals);
      return [...ng, 255]
    }
    if (this._blueIntervals) {
      nb = this._normalize(b, this._blueRange[0], this._blueRange[1], this._blueIntervals);
      return [...nb, 255];
    }

    return [
      this._normalize(r, this._redRange[0], this._redRange[1], this._redIntervals),
      this._normalize(g, this._greenRange[0], this._greenRange[1], this._greenIntervals),
      this._normalize(b, this._blueRange[0], this._blueRange[1], this._blueIntervals),
      255];
  }

  /** Normalize value to RGB using intervals or linear scaling */
  _normalize(value, min, max, intervals) {
    if (intervals) {
      // Use interval color if defined
      for (const interval of intervals) {
        if (value >= interval.min && value <= interval.max) {
          return interval.color;
        }
      }
      // Clamp to nearest interval
      if (value < intervals[0].min) return intervals[0].color;
      return intervals[intervals.length - 1].color;
    } else {
      return Math.round(((value - min) / (max - min)) * 255);
    }
  }

  computeImage(e) {
    const { redPts, greenPts, bluePts, width, height, maxD } = e.data;
    const dm = maxD * maxD;
    const imageData = new Uint8ClampedArray(width * height * 4);
    imageData.fill(0); // Initialize all pixels as transparent (alpha = 0)

    const buildGrid = (pts) => {
      if (dm <= 0) return { grid: null, cellSize: 0 };
      const cellSize = maxD;
      const grid = new Map();
      pts.forEach(pt => {
        const key = `${Math.floor(pt[0]/cellSize)},${Math.floor(pt[1]/cellSize)}`;
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push(pt);
      });
      return { grid, cellSize };
    };

    const redGrid = buildGrid(redPts);
    const greenGrid = buildGrid(greenPts);
    const blueGrid = buildGrid(bluePts);

    // Modified to return both the interpolated value and a "hasData" flag
    const computeChannel = (x, y, pts, grid) => {
      let t = 0, b = 0;
      let hasData = false; // Flag to indicate if within radius

      if (dm > 0 && grid.grid) {
        const cellX = Math.floor(x / grid.cellSize);
        const cellY = Math.floor(y / grid.cellSize);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const key = `${cellX + dx},${cellY + dy}`;
            const cell = grid.grid.get(key);
            if (!cell) continue;
            for (const pt of cell) {
              const dxPx = x - pt[0], dyPx = y - pt[1];
              const d = dxPx * dxPx + dyPx * dyPx;
              if (d > dm) continue;
              hasData = true; // Mark pixel as within radius
              if (d === 0) {
                // t = pt[2];
                // b = 1;
                // break;
                t += pt[2];
                b += 1;
              } else { // Add inverse-squared only if d ≠ 0
                const inv = 1 / d;
                t += inv * pt[2];
                b += inv;
              }
            }
          }
        }
      } else {
        for (const pt of pts) {
          const dx = x - pt[0], dy = y - pt[1];
          const d = dx * dx + dy * dy;
          if (d > dm) continue;
          hasData = true; // Mark pixel as within radius
          if (d === 0) {
            // t = pt[2];
            // b = 1;
            // break;
            t += pt[2];
            b += 1;
          } else { // Add inverse-squared only if d ≠ 0
            const inv = 1 / d;
            t += inv * pt[2];
            b += inv;
          }
        }
      }
      return { value: b > 0 ? t / b : 0, hasData };
    };

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // Compute channels and check for proximity in one pass
        const rResult = computeChannel(x, y, redPts, redGrid);
        const gResult = computeChannel(x, y, greenPts, greenGrid);
        const bResult = computeChannel(x, y, bluePts, blueGrid);

        // Determine alpha based on any channel having data
        const alpha = (rResult.hasData || gResult.hasData || bResult.hasData) ? 255 : 0;

        // Apply scaling
        const r = this._redScale(rResult.value);
        const g = this._greenScale(gResult.value);
        const b = this._blueScale(bResult.value);

        // Set color and alpha
        const color = this.getColor(r, g, b);
        color[3] = alpha;
        const pos = (y * width + x) * 4;
        imageData.set(color, pos);
      }
    }

    return { type: 'image', data: imageData, width, height };
  }

  onImageData(imageData) {
    const canvas = document.createElement('canvas');
    canvas.width = imageData.width;
    canvas.height = imageData.height;
    const ctx = canvas.getContext('2d');
    ctx.putImageData(new ImageData(imageData.data, imageData.width, imageData.height), 0, 0);
    this._canvas.getContext('2d').drawImage(canvas, 0, 0, this._canvas.width, this._canvas.height);
    if (this.worker) this.dispatchEvent({ type: 'drawend' });
    this.changed();
  }
};
export default ol_source_IDW;